import { Button, Select, SelectItem, TextField, Typography } from '@themenu/design-system'
import { type ChangeEvent, type FC, type FormEvent, useState } from 'react'
import { useIntl } from 'react-intl'

import type { ImportedIdentity } from '../../../types/brActivation'
import { ActivationStepLayout } from './ActivationStepLayout'

const GENDER_OPTIONS = [
  { key: 'MISSUS', labelId: 'br_activation.personal.gender_female' },
  { key: 'MISTER', labelId: 'br_activation.personal.gender_male' },
] as const

export type NamesStepProps = {
  imported?: ImportedIdentity | null
  onSubmit: (personal: { given_name: string; family_name: string; title?: string }) => Promise<void>
  errorMessageId?: string | null
  showBackButton?: boolean
  onBack?: () => void
}

/**
 * Step 6 — names information. Mirrors the look & feel of `ProfileForm` but is
 * a dedicated form (the profile `ProfileFormData` has no `title` and includes
 * email/locale/phone we must not show here):
 *  - first name (given_name): required,
 *  - last name (family_name): required,
 *  - title (gender): pre-filled from the migrated identity, selectable from a
 *    two-option dropdown (Femme/Homme) ISO to the validated `invitations-webapp`
 *    design, OPTIONAL,
 *  - email is removed (handled by step 7 when needed).
 *
 * All fields are pre-filled from the imported-identity echo carried on the
 * `names` step (`data.imported`, contract §7 — reorder 2026-06-11).
 *
 * `title` is a closed enum on the back-end (Account#title: MISTER/MISSUS/UNKNOWN),
 * so it is a dropdown — never a free-form field — to guarantee the value the
 * back-end will accept.
 */
export const NamesStep: FC<NamesStepProps> = ({
  imported,
  onSubmit,
  errorMessageId = null,
  showBackButton = false,
  onBack,
}) => {
  const { formatMessage } = useIntl()

  const [givenName, setGivenName] = useState(imported?.given_name ?? '')
  const [familyName, setFamilyName] = useState(imported?.family_name ?? '')
  const [title, setTitle] = useState(imported?.title ?? '')
  const [submitting, setSubmitting] = useState(false)

  const isValid = givenName.trim().length > 0 && familyName.trim().length > 0

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault()
    if (!isValid || submitting) {
      return
    }
    setSubmitting(true)
    try {
      await onSubmit({
        given_name: givenName.trim(),
        family_name: familyName.trim(),
        ...(title ? { title } : {}),
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <ActivationStepLayout
      titleId="br_activation.personal.title"
      errorMessageId={errorMessageId}
      showBackButton={showBackButton}
      onBack={onBack}
      onSubmit={handleSubmit}
      testId="br-activation-personal-form"
      footer={
        <Button
          data-testid="br-activation-personal-submit"
          type="submit"
          variant="primary"
          className="w-full"
          disabled={!isValid || submitting}
          loading={submitting}
        >
          {formatMessage({ id: 'br_activation.personal.submit' })}
        </Button>
      }
    >
      <Typography variant="body">{formatMessage({ id: 'br_activation.personal.description' })}</Typography>
      <Select
        size={56}
        id="br-activation-title"
        name="title"
        data-testid="br-activation-title"
        label={formatMessage({ id: 'br_activation.personal.gender' })}
        selectedKey={title || null}
        onChange={(value: string) => setTitle(value)}
        autocomplete={false}
      >
        {GENDER_OPTIONS.map((option) => (
          <SelectItem key={option.key}>{formatMessage({ id: option.labelId })}</SelectItem>
        ))}
      </Select>
      <TextField
        size={56}
        id="br-activation-given-name"
        name="given_name"
        data-testid="br-activation-given-name"
        label={formatMessage({ id: 'profile.field.first_name' })}
        value={givenName}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setGivenName(e.target.value)}
        required
        autoCapitalize="words"
      />
      <TextField
        size={56}
        id="br-activation-family-name"
        name="family_name"
        data-testid="br-activation-family-name"
        label={formatMessage({ id: 'profile.field.last_name' })}
        value={familyName}
        onChange={(e: ChangeEvent<HTMLInputElement>) => setFamilyName(e.target.value)}
        required
        autoCapitalize="words"
      />
    </ActivationStepLayout>
  )
}
